// Transport DTOs and models for Managed Attendance (Marcas Gestionadas)
import type { LC_PagedResult } from '@/api/shared/types';
import type { BaseOrderDirection } from './shared.types';

// --- Action and Status Types ---

/**
 * Action types for managed attendance records
 */
export type ManagedAttendanceAction =
  | 'created'
  | 'disabled'
  | 'modified'
  | 'incident';

/**
 * Status of a managed attendance record
 */
export type ManagedAttendanceStatus = 'pending' | 'approved' | 'rejected';

/**
 * Event types for attendance records
 */
export type AttendanceEventType =
  | 'shift_start'
  | 'shift_end'
  | 'entry'
  | 'exit'
  | 'break_start'
  | 'break_end'
  | 'overtime_start'
  | 'overtime_end';

// --- Translation Constants ---

/**
 * Maps ManagedAttendanceStatus to translation keys
 */
export const ATTENDANCE_STATUS_MAP: Record<ManagedAttendanceStatus, string> = {
  pending: 'attendance:managedAttendance.status.pending',
  approved: 'attendance:managedAttendance.status.approved',
  rejected: 'attendance:managedAttendance.status.rejected',
};

/**
 * Maps ManagedAttendanceAction to translation keys
 */
export const ATTENDANCE_ACTION_MAP: Record<ManagedAttendanceAction, string> = {
  created: 'attendance:managedAttendance.actions.created',
  disabled: 'attendance:managedAttendance.actions.disabled',
  modified: 'attendance:managedAttendance.actions.modified',
  incident: 'attendance:managedAttendance.actions.incident',
};

/**
 * Maps AttendanceEventType to translation keys
 */
export const ATTENDANCE_EVENT_TYPE_MAP: Record<AttendanceEventType, string> = {
  shift_start: 'attendance:managedAttendance.events.shiftStart',
  shift_end: 'attendance:managedAttendance.events.shiftEnd',
  entry: 'attendance:managedAttendance.events.entry',
  exit: 'attendance:managedAttendance.events.exit',
  break_start: 'attendance:managedAttendance.events.breakStart',
  break_end: 'attendance:managedAttendance.events.breakEnd',
  overtime_start: 'attendance:managedAttendance.events.overtimeStart',
  overtime_end: 'attendance:managedAttendance.events.overtimeEnd',
};

/**
 * Factory function to create translation helpers for attendance status, actions, and event types
 * @param translate - Translation function from i18n hook
 * @returns Object with helper methods for translating attendance values
 */
export const createAttendanceTranslationHelpers = (translate: (key: string) => string) => ({
  translateStatus: (status: ManagedAttendanceStatus): string => {
    const key = ATTENDANCE_STATUS_MAP[status];
    return translate(key);
  },

  translateAction: (action: ManagedAttendanceAction): string => {
    const key = ATTENDANCE_ACTION_MAP[action];
    return translate(key);
  },

  translateEventType: (eventType: AttendanceEventType): string => {
    const key = ATTENDANCE_EVENT_TYPE_MAP[eventType];
    return translate(key) || eventType;
  },
});

// --- Transport DTOs ---

export interface LC_GetManagedAttendanceMarksListItemOut {
  markId: string | null;
  employeeRut: string | null;
  workdayDate: string | null;
  markTime: string | null;
  status: string | null; // Backend values: 'Pendiente', 'Aprobado', 'Rechazado'
  contractCompany: string | null;
  createdDate: string | null;
  eventName: string | null;
  actionName: string | null;
  actionDate: string | null;
  originalMarkTime: string | null; // Time value for modified markings
  rejectionComment: string | null;
  modifiedBy: string | null;
}

/**
 * Input DTO for approving or rejecting a managed mark
 */
export interface LC_UpdateManagedMarkStatusIn {
  status: string;
  comment: string;
}

/**
 * Output DTO for updating a managed mark status
 */
export type LC_UpdateManagedMarkStatusOut = Record<string, never>;

export type LC_GetManagedAttendanceMarksOut =
  LC_PagedResult<LC_GetManagedAttendanceMarksListItemOut>;

export interface LC_GetManagedAttendanceIn {
  page?: number;
  pageSize?: number;
  textSearch?: string | null; // New: search by RUT or event name
  orderBy?: string | null; // workdayDate | markTime | status | actionName |
  //  eventName | createdDate | actionDate
  orderDirection?: BaseOrderDirection | null; // Sort direction: ASC or DESC
  dateFrom?: string | null;                // YYYY-MM-DD format
  dateTo?: string | null;                  // YYYY-MM-DD format
  status?: string | null;
  actionName?: string | null;
}

// --- Internal UI Models ---

/**
 * Managed attendance record model for UI
 */
export interface ManagedAttendanceRecord {
  id: string;
  markId: string | null;
  creationDate: string; // dd/MM/yyyy - Fecha de creación del registro
  action: ManagedAttendanceAction;
  workdayDate: string; // dd/MM/yyyy - Fecha de la jornada
  markingTime: string; // HH:mm:ss - Hora del marcaje
  // Nombre del evento (e.g., "Entrada a oficina", "Salida Horas Extra",
  // "Sin evento")
  eventName: string;
  originalMarkTime: string | null; // HH:mm:ss - Valor original para marcajes modificados
  modificationDate: string | null; // dd/MM/yyyy - Fecha de aprobación/rechazo
  status: ManagedAttendanceStatus;
  // Campos adicionales para el detalle
  fullName: string; // Nombre completo del empleado
  area: string; // Área del empleado
  location: string; // Ubicación
  errorType: string; // Tipo de error
  comment: string | null; // Comentario
  rejectionComment: string | null; // Comentario de rechazo
  creatorUser: string | null; // Usuario que creó/eliminó el marcaje
  actionDate: string | null; // dd/MM/yyyy - Fecha de aprobación/rechazo
}

/**
 * Filters for managed attendance list
 */
export interface ManagedAttendanceFilters {
  from?: string;
  to?: string;
  action?: ManagedAttendanceAction | 'all' | null;
  status?: ManagedAttendanceStatus | 'all' | null;
}

/**
 * Paginated response for managed attendance
 */
export interface ManagedAttendancePage {
  data: ManagedAttendanceRecord[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}
